import type {ReactElement} from 'react';
import React, {useEffect, useRef} from 'react';
import styles from './ai-prompt-box.module.scss';
import type {AiPromptBoxProps} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {AiPromptBoxSize, PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {nextPromptBoxKey} from '@Components/ai-prompt-box/libraries/ai-prompt-box-key.library';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {useAiPromptBoxSubmitFlow} from '@Components/ai-prompt-box/hooks/useAiPromptBoxSubmitFlow';
import {AiPromptBoxInputField} from '@Components/ai-prompt-box/components/ai-prompt-box-input-field';
import {AiPromptBoxIssue} from '@Components/ai-prompt-box/components/ai-prompt-box-issue';
import {AiPromptBoxSuggestions} from '@Components/ai-prompt-box/components/ai-prompt-box-prompt-suggestions';
import {AnimatePresence, motion} from 'framer-motion';
import {AnimatedBorderElement} from '@Components/animated-border-element/animated-border-element';
import {AiPromptBoxAttachments} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments';
import {PromptSuggestionPillPosition} from '@Components/ai-prompt-box/components/ai-prompt-box-prompt-suggestions/ai-prompt-box-prompt-suggestions.types';
import {usePromptBoxAutoActivate} from '@Components/ai-prompt-box/hooks/usePromptBoxAutoActivate';
import {Text, TextSize} from '@Components/text';
import {AiPromptBoxFooterRightActions} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-right-actions';
import {AnimatedAiPromptBoxSnackbar} from '@Components/ai-prompt-box/components/ai-prompt-box-snackbar/ai-prompt-box-snackbar';
import {isPromptBoxProcessing} from '@Components/ai-prompt-box/ai-prompt-box-library';
import {AiPromptBoxFooterLeftActions} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-left-actions';
import {useProTips} from '@Components/ai-prompt-box/hooks/useProTips';
import {AiPromptBoxProTip} from '@Components/ai-prompt-box/components/ai-prompt-box-pro-tip/ai-prompt-box-pro-tip';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {aiPromptBoxLayoutContext} from '@Components/ai-prompt-box/ai-prompt-box-layout.context';
import {AI_PROMPT_BOX_MOBILE_QUERY} from '@Components/ai-prompt-box/ai-prompt-box.types';

export function AiPromptBox({onGenerate, onRegenerate, onStop, onFocus, onDocumentClick, ...props}: AiPromptBoxProps): ReactElement | null {
  const promptBoxRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const isMobile = useContainerQuery(promptBoxRef, AI_PROMPT_BOX_MOBILE_QUERY).mobile ?? false;
  const {state, actions, isInitialized} = useAiPromptBox(props.id, props.initialConfig, isMobile);

  const isLoading = isPromptBoxProcessing(state.promptBoxState);
  const isSingleLineVariant = state.size === AiPromptBoxSize.SINGLE_LINE;
  const doShowInitialAnimation = state.showInitialAnimation ?? true;
  const SINGLE_LINE_BORDER_RADIUS = 28;
  const MULTI_LINE_BORDER_RADIUS = 12;
  const MOBILE_WIDTH_BREAKPOINT = 992;

  const {activeProTip, dismissActiveProTip} = useProTips({
    enabled: props.showProtips ?? false,
    prompt: state.prompt,
    attachmentCount: state.attachments?.items?.length ?? 0,
    context: props.proTipContext,
    isMobile,
  });

  usePromptBoxAutoActivate({
    promptBoxState: state.promptBoxState,
    setPromptBoxState: actions.update,
    disableAutoActivateOnWidth: {
      maxWidth: MOBILE_WIDTH_BREAKPOINT,
    },
    disabled: props.disableAutoActivate,
  });

  const {submit: handleSubmission, cancelSoftSend} = useAiPromptBoxSubmitFlow(props.id, onGenerate);

  const handledSubmitKeyRef = useRef(state.submitKey);

  useEffect(() => {
    if (state.submitKey === undefined || state.submitKey === handledSubmitKeyRef.current) {
      return;
    }

    handledSubmitKeyRef.current = state.submitKey;
    void handleSubmission();
  }, [state.submitKey, handleSubmission]);

  const handleFocus = (): void => {
    if (!isLoading) {
      actions.update({promptBoxState: PromptBoxState.ACTIVE});
    }
    onFocus?.();
  };

  const getInputBox = (): ReactElement => {
    return (
      <AnimatedBorderElement
        animationDurationMs={2200}
        radius={isSingleLineVariant ? SINGLE_LINE_BORDER_RADIUS : MULTI_LINE_BORDER_RADIUS}
        width={2}
        animationCount={doShowInitialAnimation ? 1 : 0}
      >
        <div
          data-testid={props['data-testid']}
          className={`
            ${styles.promptBox}
            _full-width
            ${props.blockClasses ?? ''}
            ${state.promptBoxState === PromptBoxState.ACTIVE || state.promptBoxState === PromptBoxState.REGENERATE ? styles.active : ''} 
            ${isLoading ? styles.promptBoxLoading : ''} 
          `}
        >
          <AiPromptBoxAttachments id={props.id} onDocumentClick={onDocumentClick} />
          <div className={state.isRecordingAudio || state.isProcessingAudio ? styles.disabledInteraction : ''}>
            <AiPromptBoxInputField
              id={props.id}
              inputTestId={props.inputTestId}
              handleSubmission={handleSubmission}
              focus={props.focus}
              inputClasses={props.inputClasses}
              onFocus={handleFocus}
              allowSoftSendDuringUploads={props.allowSoftSendDuringUploads}
              spellCheck={props.spellCheck}
            />
          </div>
          <div className={'_full-width'} ref={footerRef}>
            <div className={`${styles.leftContainer} ${isSingleLineVariant ? styles.singleLine : ''} ${state.isRecordingAudio || state.isProcessingAudio ? '_hidden' : ''}`}>
              <AiPromptBoxFooterLeftActions id={props.id} size={state.size} options={props.options} footerRef={footerRef} />
            </div>
            <div
              className={`${styles.rightContainer} ${isSingleLineVariant ? styles.singleLine : ''} ${state.isRecordingAudio || state.isProcessingAudio ? styles.fullWidth : ''}`}
            >
              <AiPromptBoxFooterRightActions
                id={props.id}
                submitTestId={props.submitTestId}
                onSubmit={handleSubmission}
                onStop={onStop}
                onSoftStop={cancelSoftSend}
                onRegenerate={onRegenerate}
                allowSoftSendDuringUploads={props.allowSoftSendDuringUploads}
              />
            </div>
          </div>
        </div>
      </AnimatedBorderElement>
    );
  };

  const getPromptSuggestions = (): ReactElement | null => {
    if (state.prompt) {
      return null;
    }

    const hasSuggestions = state.promptSuggestions?.suggestions?.length ?? !!state.promptSuggestions?.recentPrompt;
    const shouldShow = hasSuggestions && !state.error && state.promptSuggestions?.visible;

    if (!shouldShow) {
      return null;
    }

    return <AiPromptBoxSuggestions id={props.id} />;
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>): void => {
    const nextFocused = event.relatedTarget as HTMLElement | null;

    if (promptBoxRef.current?.contains(nextFocused)) {
      return;
    }

    if (!isPromptBoxProcessing(state.promptBoxState) && !state.prompt) {
      actions.update({promptBoxState: PromptBoxState.IDLE});
    }
  };

  const renderDailyLimitGrowl = (): ReactElement => {
    return (
      <AnimatePresence>
        {state.showDailyLimitError ? (
          <motion.div
            initial={{opacity: 0, y: 25, x: '-50%'}}
            animate={{opacity: 1, y: -10, x: '-50%'}}
            exit={{opacity: 0, y: 25, x: '-50%'}}
            transition={{duration: 0.3}}
            className={`spacing-p-l-2 spacing-p-r-2 spacing-p-t-1 spacing-p-b-1 radius-8 _position-absolute ${styles.dailyLimitGrowl}`}
          >
            <Text val={window.i18next.t('pmwjs_daily_limit_reached')} size={TextSize.SMALL} bold className="content-body-white" />
          </motion.div>
        ) : null}
      </AnimatePresence>
    );
  };

  const focusPromptBox = (): void => {
    actions.update({promptBoxState: PromptBoxState.ACTIVE, focusKey: nextPromptBoxKey()});
  };

  const renderProTip = (): ReactElement => {
    return <AnimatePresence>{activeProTip ? <AiPromptBoxProTip {...activeProTip} onDismiss={dismissActiveProTip} onActivate={focusPromptBox} /> : null}</AnimatePresence>;
  };

  const renderFinishingUploadsSnackbar = (): ReactElement => {
    return (
      <AnimatedAiPromptBoxSnackbar
        visible={state.isFinishingUploads ?? false}
        className={styles.finishingUploadsSnackbar}
        text={window.i18next.t('pmwjs_ai_chat_message_will_send_when_uploads_ready')}
      />
    );
  };

  const renderDisclaimerIfNeeded = (): ReactElement | null => {
    if (!state.showDisclaimer) {
      return null;
    }

    return (
      <div className={`spacing-p-b-1 ${styles.disclaimerContainer}`}>
        <Text val={window.i18next.t('pmwjs_ai_can_make_mistakes')} className={`_text-center _full-width ${styles.disclaimerText}`} size={TextSize.XXSMALL} />
      </div>
    );
  };

  const renderAnimatedSuggestions = (): ReactElement => {
    return <AnimatePresence initial={false}>{getPromptSuggestions()}</AnimatePresence>;
  };

  const getPromptBox = (): ReactElement => {
    const showSuggestionsAbove = state.promptSuggestions?.position === PromptSuggestionPillPosition.ABOVE;

    return (
      <aiPromptBoxLayoutContext.Provider value={isMobile}>
        <div className={props.containerClasses}>
          <div ref={promptBoxRef} tabIndex={-1} onBlur={handleBlur} className={styles.promptBoxContainer}>
            {renderDailyLimitGrowl()}
            {renderProTip()}
            {renderFinishingUploadsSnackbar()}
            {showSuggestionsAbove ? renderAnimatedSuggestions() : null}
            {getInputBox()}
            <AiPromptBoxIssue id={props.id} retry={() => void onRegenerate()} />
            {showSuggestionsAbove ? null : renderAnimatedSuggestions()}
          </div>
          {renderDisclaimerIfNeeded()}
        </div>
      </aiPromptBoxLayoutContext.Provider>
    );
  };

  return isInitialized ? getPromptBox() : null;
}
